import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { proxyImg } from '@/lib/imageProxy';

/**
 * MallBannerCarousel — full-width rotating banner showcase for the home page.
 *
 * Uses real Inorbit campaign banners (scraped from inorbitonline.com) and
 * frames them with mall-themed overlays — "Now playing across all malls",
 * marquee copy, store credit. Designed to evoke the in-mall LED billboards
 * that loop curated brand campaigns.
 */
export default function MallBannerCarousel({ banners = [], mallName, mallSlug }) {
    const [idx, setIdx] = useState(0);
    // Keep last-known banners during mall switch so we don't flash empty/blue
    const [stableBanners, setStableBanners] = useState(banners);

    useEffect(() => {
        if (banners.length > 0) {
            setStableBanners(banners);
            setIdx(0);
        }
    }, [banners, mallSlug]);

    const slides = stableBanners.slice(0, 6);

    useEffect(() => {
        if (slides.length <= 1) return;
        const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6000);
        return () => clearInterval(t);
    }, [slides.length]);

    const go = (dir) => setIdx((i) => (i + dir + slides.length) % slides.length);

    if (slides.length === 0) {
        return (
            <section className="relative bg-inorbit-blue-deeper py-10 md:py-14" data-testid="mall-banner-carousel-loading">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="aspect-[16/7] md:aspect-[21/8] rounded-md bg-inorbit-blue/40 animate-pulse" />
                </div>
            </section>
        );
    }

    return (
        <section className="relative bg-inorbit-blue-deeper py-10 md:py-14 overflow-hidden" data-testid="mall-banner-carousel">
            {/* film-grain seam */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-inorbit-yellow/40 to-transparent" />

            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex items-end justify-between gap-4 mb-6">
                    <div>
                        <div className="overline text-inorbit-yellow mb-2 flex items-center gap-3">
                            <span className="inline-block w-2 h-2 rounded-full bg-inorbit-orange animate-pulse" />
                            Now Playing on the Mall Billboard
                        </div>
                        <h2 className="font-serif text-3xl md:text-5xl text-white leading-tight tracking-tight">
                            This week at <span className="text-inorbit-yellow">{mallName || 'Inorbit'}</span>
                        </h2>
                    </div>
                    <div className="hidden md:flex items-center gap-2">
                        <button
                            onClick={() => go(-1)}
                            className="w-11 h-11 rounded-full border border-white/30 text-white hover:bg-inorbit-yellow hover:text-inorbit-blue hover:border-inorbit-yellow transition-all flex items-center justify-center"
                            data-testid="banner-prev"
                            aria-label="Previous"
                        >
                            <ChevronLeft size={18} strokeWidth={2} />
                        </button>
                        <button
                            onClick={() => go(1)}
                            className="w-11 h-11 rounded-full border border-white/30 text-white hover:bg-inorbit-yellow hover:text-inorbit-blue hover:border-inorbit-yellow transition-all flex items-center justify-center"
                            data-testid="banner-next"
                            aria-label="Next"
                        >
                            <ChevronRight size={18} strokeWidth={2} />
                        </button>
                    </div>
                </div>

                {/* Stage */}
                <div className="relative">
                    {/* Main slide */}
                    <Link
                        to={slides[idx]?.link || '/shop'}
                        className="block aspect-[16/7] md:aspect-[21/8] relative overflow-hidden rounded-md group bg-inorbit-blue"
                        data-testid={`banner-slide-${idx}`}
                    >
                        {slides.map((b, i) => (
                            <img
                                key={`${b.image}-${i}`}
                                src={proxyImg(b.image)}
                                alt={b.alt || 'Inorbit campaign'}
                                className={`absolute inset-0 w-full h-full object-cover transition-all duration-1000 ${i === idx ? 'opacity-100 scale-100' : 'opacity-0 scale-105'}`}
                                loading={i === 0 ? 'eager' : 'lazy'}
                            />
                        ))}
                        <div className="absolute inset-0 bg-gradient-to-t from-inorbit-blue-deeper/80 via-transparent to-transparent" />
                        <div className="absolute bottom-0 inset-x-0 p-6 md:p-10 text-white">
                            <div className="overline text-inorbit-yellow mb-2 hidden md:block">Live Campaign</div>
                            <div className="font-serif text-xl md:text-3xl leading-tight max-w-xl">
                                {(slides[idx]?.alt || 'Inorbit Edit')
                                    .replace(/-/g, ' ')
                                    .replace(/\b\w/g, (l) => l.toUpperCase())
                                    .replace(/\d{4}/, '')}
                            </div>
                            <div className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-inorbit-yellow font-semibold">
                                Tap to shop
                                <ChevronRight size={14} />
                            </div>
                        </div>
                    </Link>

                    {/* Thumb strip */}
                    <div className="mt-4 grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="banner-thumbs">
                        {slides.map((b, i) => (
                            <button
                                key={`thumb-${i}`}
                                onClick={() => setIdx(i)}
                                className={`relative aspect-[16/9] overflow-hidden rounded transition-all ${i === idx ? 'ring-2 ring-inorbit-yellow scale-[1.02]' : 'opacity-60 hover:opacity-100'}`}
                                data-testid={`banner-thumb-${i}`}
                                aria-label={`Slide ${i + 1}`}
                            >
                                <img
                                    src={proxyImg(b.image)}
                                    alt=""
                                    className="absolute inset-0 w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </button>
                        ))}
                    </div>

                    {/* Slide counter (pill) */}
                    <div className="absolute top-4 right-4 bg-black/40 backdrop-blur-md text-white text-xs px-3 py-1.5 rounded-full font-medium tracking-wider">
                        {String(idx + 1).padStart(2, '0')} <span className="opacity-50">/ {String(slides.length).padStart(2, '0')}</span>
                    </div>
                </div>
            </div>
        </section>
    );
}
